import Link from 'next/link'
import {useTranslation} from 'next-i18next'
import React, {useRef, useState} from 'react'
import {Box, CallToAction, Container} from '@miro-site/core'
import {
  CardGrid,
  Category,
  CategoryGroup,
  TemplateCard,
  TemplateCardProps,
} from '@miro-site/templates-design'
import {TemplateLoader, TemplateOpener} from '@miro-site/ui-foundation'
import {TemplateCardData} from '../data/mappers/toTemplateCard'
import TemplatesSearch from './TemplatesSearch'

export interface CategoryPageProps {
  slug: string
  title: string
  description: string
  templates: TemplateCardData[]
  categoryGroups: CategoryGroup[]
}

const CategoryPage: React.FC<CategoryPageProps> = ({
  title,
  description,
  templates = [],
  categoryGroups = [],
}) => {
  const [metaId, setMetaId] = useState(null)
  const [templateId, setTemplateId] = useState(null)

  const headerRef = useRef<HTMLElement>(null)

  const {t} = useTranslation('common')
  const getActionButtonProps = ({
    appSlug,
    newBoardMetadataId,
    accessConstraints,
  }: TemplateCardData): TemplateCardProps['actionButtonProps'] => {
    if (accessConstraints?.requiredPlans?.length > 0) {
      return {
        children: t('templates-actions-open-template'),
        onClick: e => {
          e.preventDefault()
          setMetaId(null)
          setTemplateId(appSlug)
        },
      }
    }

    return {
      onClick: e => {
        e.preventDefault()
        setTemplateId(null)
        setMetaId(newBoardMetadataId)
      },
    }
  }

  return (
    <>
      <main data-selected-meta-id={metaId} data-selected-appslug={templateId}>
        <Box $mt={30} $mb={60}>
          <TemplatesSearch
            withBackButton
            categoryGroups={categoryGroups}
            stickyFixingPoint={headerRef}
          />
        </Box>
        <Container ref={headerRef}>
          <Category.Title>{title}</Category.Title>
          <Category.Description>{description}</Category.Description>
        </Container>
        {templates.length > 0 && (
          <CardGrid $pt={20}>
            {templates.map((tpl, i) => (
              <TemplateCard
                templateActions={{
                  PREVIEW: t('templates-actions-preview'),
                  USE_TEMPLATE: t('templates-actions-use-template'),
                  SHOW_MORE_INFO: t('templates-actions-more-info'),
                }}
                key={tpl.templatePagePath}
                previewButton={defaultButton => (
                  <Link href={tpl.templatePagePath} passHref legacyBehavior>
                    {defaultButton}
                  </Link>
                )}
                actionButtonProps={getActionButtonProps(tpl)}
                position={i}
                {...tpl}
              />
            ))}
          </CardGrid>
        )}
        <CallToAction
          background="var(--wcds-templates-cta-background-color)"
          border="1px solid var(--wcds-templates-cta-border-color)"
          heading={t('signup-cta-headline')}
          content={t('signup-cta-description')}
          button={{
            children: t('signup-cta-button'),
            href: '/signup',
          }}
        />
      </main>
      <TemplateLoader metadataId={metaId} setMetadataId={setMetaId} />
      <TemplateOpener templateId={templateId} setTemplateId={setTemplateId} />
    </>
  )
}

export default CategoryPage
